<template>
  <div class="flex flex-col h-full">
    <div class="flex flex-col gap-2 overflow-y-auto">
      <span class="text-xs text-gray-500 mb-2">בחרו צבע לסימון הלקוח. ניתן להוסיף תווית לכל צבע (התוויות משותפות לכל הלקוחות בעסק).</span>

      <div
        v-for="colorOption in CUSTOMER_MARKER_COLORS"
        :key="colorOption.key"
        class="flex items-center gap-2"
      >
        <div
          class="flex-1 flex items-center px-3 py-2 rounded-lg cursor-pointer min-h-[2.25rem]"
          :class="[colorOption.key === CUSTOMER_MARKER_COLOR_DEFAULT ? 'border border-gray-800' : '']"
          :style="{ backgroundColor: colorOption.value, border: colorOption.key === selectedColor ? `2px solid ${getColorVariants(colorOption.value).darker}` : colorOption.key === CUSTOMER_MARKER_COLOR_DEFAULT ? '1px solid black' : '' }"
          @click="selectColor(colorOption.key)"
        >
          <input
            v-if="editingColorKey === colorOption.key"
            :ref="el => setEditingInput(el as HTMLInputElement | null)"
            v-model="editingLabelText"
            maxlength="20"
            class="text-sm font-medium w-full bg-transparent border-0 outline-none p-0 focus:ring-0"
            :class="isLightColor(colorOption.value) ? 'text-gray-900 placeholder-gray-700' : 'text-white placeholder-white/70'"
            placeholder="שם התווית"
            @click.stop
            @keydown.enter.prevent="commitLabel"
            @keydown.esc.prevent="cancelLabel"
            @blur="commitLabel"
          />
          <span
            v-else
            class="text-sm font-medium truncate"
            :class="isLightColor(colorOption.value) ? 'text-gray-900' : 'text-white'"
          >
            {{ labels[colorOption.key] || '' }}
          </span>
        </div>


        <i
          v-if="colorOption.key !== CUSTOMER_MARKER_COLOR_DEFAULT"
          :class="[
            'pi flex-none text-gray-500 cursor-pointer p-2',
            editingColorKey === colorOption.key ? 'pi-check' : 'pi-pencil',
          ]"
          @click.stop="toggleEditingLabel(colorOption.key)"
        />
        <span v-else class="flex-none w-9" />
      </div>
    </div>

    <drawer-footer
      class="mt-auto"
      approve-label="שמירה"
      :approve-disable="loading"
      :approve-loading="loading"
      @approve-click="save"
      @cancel-click="$emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue';
import {
  CUSTOMER_MARKER_COLORS,
  CUSTOMER_MARKER_COLOR_DEFAULT
} from '@torim-app/types';
import { getColorVariants } from '@torim-app/utils';

const props = defineProps<{
  initialColor?: string;
  initialLabels?: Record<string, string>;
  onSave: (payload: { color: string; labels: Record<string, string> }) => Promise<void>;
}>();

const emit = defineEmits(['close']);
const selectedColor = ref<string>(props.initialColor || CUSTOMER_MARKER_COLOR_DEFAULT);
const labels = ref<Record<string, string>>({ ...(props.initialLabels ?? {}) });
const loading = ref(false);

const editingColorKey = ref<string>('');
const editingLabelText = ref<string>('');
const editingInputRef = ref<HTMLInputElement | null>(null);

const setEditingInput = (el: HTMLInputElement | null) => {
  editingInputRef.value = el;
};

const selectColor = (color: string) => {
  selectedColor.value = color;
};

const toggleEditingLabel = async (color: string) => {
  if (editingColorKey.value === color) {
    commitLabel();
    return;
  }
  selectedColor.value = color;
  editingColorKey.value = color;
  editingLabelText.value = labels.value[color] ?? '';
  await nextTick();
  editingInputRef.value?.focus();
  editingInputRef.value?.select();
};

const commitLabel = () => {
  if (!editingColorKey.value) return;
  const trimmed = editingLabelText.value.trim();
  if (trimmed) {
    labels.value[editingColorKey.value] = trimmed;
  } else {
    delete labels.value[editingColorKey.value];
  }
  editingColorKey.value = '';
  editingLabelText.value = '';
};

const cancelLabel = () => {
  editingColorKey.value = '';
  editingLabelText.value = '';
};

const isLightColor = (hex: string): boolean => {
  const value = hex.replace('#', '');
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6;
};

const save = async () => {
  loading.value = true;
  try {
    delete labels.value[CUSTOMER_MARKER_COLOR_DEFAULT];
    await props.onSave({ color: selectedColor.value, labels: { ...labels.value } });
    emit('close');
  } finally {
    loading.value = false;
  }
};
</script>
